import { ImageLibraryPickerModal } from '@/components/image-library/image-library-picker-modal';
import { insertImageFromLibrarySelection } from '@/lib/image-from-library';
import type { Editor } from '@tiptap/react';
import { useCallback, useMemo, useState } from 'react';
import ZyDragHandler from './tiptap-extension/drag-handler-extension/drag-handler.tsx';
import { ZyFloatingMenu } from './tiptap-extension/floating-menu-extension/floating-menu.tsx';
import { TableNodeUiPlugins } from './tiptap-node/table-node/ui/table-node-ui-plugins.tsx';
import { TableInsertTriggerDropdown, useTableInsertTrigger } from './tiptap-node/table-node/ui/table-trigger-button';
import { CommentBubble } from './tiptap-ui/comment-button/comment-bubble';
import { LinkBubble } from './tiptap-ui/link-popover';
import { SymbolsTriggerDropdown, useSymbolsTrigger } from './tiptap-ui/symbols-trigger-button/index.ts';
import { ZyCodeInlineBubble } from './tiptap-ui/zycode-inline-bubble/zycode-inline-bubble.tsx';
import { ZydefTermDblClickPopover } from './tiptap-ui/zydefterm-popover/zydefterm-popover.tsx';
import { SlashDropdownMenu } from './tiptap-ui/zyslash-dropdown-menu/zyslash-dropdown-menu.tsx';

interface TiptapEditorPluginsProps {
    editor: Editor;
}

export function TiptapEditorPlugins({ editor }: TiptapEditorPluginsProps) {
    const [isImageLibraryModalOpen, setIsImageLibraryModalOpen] = useState(false);

    const {
        isOpen: isSymbolsTriggerOpen,
        referenceRect: symbolsTriggerReferenceRect,
        open: openSymbolsTrigger,
        close: closeSymbolsTrigger,
    } = useSymbolsTrigger();

    const {
        isOpen: isTableInsertTriggerOpen,
        referenceRect: tableInsertTriggerReferenceRect,
        insertType: tableInsertType,
        open: openTableInsertTrigger,
        close: closeTableInsertTrigger,
    } = useTableInsertTrigger();

    const openImageLibraryModal = useCallback(() => {
        queueMicrotask(() => setIsImageLibraryModalOpen(true));
    }, []);

    const subjectName = editor.state.doc.attrs.subjectName?.trim();
    const imageLibraryDefaultOnlyUnused = !subjectName;
    const imageLibraryDefaultSelectedSubjects = useMemo(() => (subjectName ? [subjectName] : []), [subjectName]);

    return (
        <>
            <ZyFloatingMenu editor={editor} />
            <ZyDragHandler editor={editor} />
            <LinkBubble editor={editor} />
            <CommentBubble editor={editor} />
            <ZydefTermDblClickPopover editor={editor} />
            <ZyCodeInlineBubble editor={editor} />
            <SlashDropdownMenu
                editor={editor}
                config={{
                    onSymbolsTrigger: openSymbolsTrigger,
                    onImageLibraryTrigger: openImageLibraryModal,
                    onTableInsertTrigger: openTableInsertTrigger,
                }}
            />
            <SymbolsTriggerDropdown
                editor={editor}
                isOpen={isSymbolsTriggerOpen}
                referenceRect={symbolsTriggerReferenceRect}
                onClose={closeSymbolsTrigger}
            />
            <TableInsertTriggerDropdown
                editor={editor}
                isOpen={isTableInsertTriggerOpen}
                referenceRect={tableInsertTriggerReferenceRect}
                insertType={tableInsertType}
                onClose={closeTableInsertTrigger}
            />
            <ImageLibraryPickerModal
                open={isImageLibraryModalOpen}
                onOpenChange={setIsImageLibraryModalOpen}
                defaultOnlyUnused={imageLibraryDefaultOnlyUnused}
                defaultSelectedSubjects={imageLibraryDefaultSelectedSubjects}
                onImageSelected={image => {
                    insertImageFromLibrarySelection(editor, image);
                }}
            />
            <TableNodeUiPlugins />
        </>
    );
}
